import i18n from '@/js/i18n';

import { getCurrentLanguage } from '@/helpers/AppHelpers';

// Convertir toute la chaîne de car. un peu comme le ferait text-transform: capitalize en css.
// Une lettre est capitalisée après un espace (" ") ou un tiret ("-")
export function capitalize(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map(capitalizeFirstLetter)
    .join(' ')
    .split('-')
    .map(capitalizeFirstLetter)
    .join('-')
    .split("'")
    .map(capitalizeFirstLetter)
    .join("'");
}

// Majuscule sur le 1er caractère seulement
export function capitalizeFirstLetter(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// Met en Minuscule sur le 1er caractère seulement
export function premiereLettreMinuscule(str) {
  return `${str}`.charAt(0).toLowerCase() + str.substr(1);
}

// Retirer les espaces redoublés
export function removeExtraSpaces(val) {
  const plusQunEspaceRegex = /\s\s+/g;
  // Si une occurence de plus d'un espace on le replace par un seul
  return val.replace(plusQunEspaceRegex, ' ');
}

// Retirer tous les caractères accentués (utile pour effectuer des recherches sans accents)
// Inspiration: https://stackoverflow.com/questions/5700636/using-javascript-to-perform-text-matches-with-without-accented-characters
//              https://github.com/shentao/vue-multiselect/issues/281#issuecomment-597718858
export function normalize(string) {
  return string
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Retourne le montant bien formatté en fonction de la langue et des cents
 *
 * @param {number} montant Montant à formatter
 * @param {string} langue Langue dans laquelle formatter le montant
 * @return {string} Retourne le montant formatté
 */
export function formatMontant(montant, langue = null) {
  langue = langue ?? i18n.locale;
  if (!montant) return '';
  if (montant % 1 == 0) {
    montant = parseFloat(montant).toFixed(0);
  } else {
    montant = parseFloat(montant).toFixed(2);
  }
  if (langue == 'fr') {
    if (montant >= 1000) {
      montant = montant.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    }
    return `${montant.toString().replace(/\./g, ',')} $`;
  }
  if (langue == 'en' && montant < 0.1) {
    return `${montant.toString().slice(-1)}¢`;
  }
  if (montant < 1) {
    return `${montant.toString().split('.')[1]}¢`;
  }
  if (montant >= 1000) {
    montant = montant.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  return `$${montant}`;
}

/**
 * Retourne le montant au format monétaire (fr/en) avec les décimales souhaitées
 *
 * @param {number} amount Montant à formatter
 * @param {number} decimalCount Nombre de décimals spuhaités
 * @return {string} Retourne le montant formatté au format Monétaire
 */
export function formatNombreEnMonetaire(amount, decimalCount = 2) {
  let montantNombre = null;
  const prefix = i18n.t('vMoney.prefix');
  const suffix = i18n.t('vMoney.suffix');

  if (typeof amount == 'string') {
    amount = amount.replace(',', '.');
  }
  decimalCount = Math.abs(decimalCount);
  decimalCount = Number.isNaN(decimalCount) ? 2 : decimalCount;

  if (getCurrentLanguage() == 'en') {
    montantNombre = (Number(amount) || 0).toFixed(decimalCount).replace(/\d(?=(\d{3})+\.)/g, '$&,');
  } else {
    montantNombre = (Number(amount) || 0).toFixed(decimalCount).replace(/\d(?=(\d{3})+,)/g, '$& ');
  }
  const montant = prefix + montantNombre + suffix;
  return montant.replace('.', i18n.t('vMoney.decimal'));
}

/**
 * Transforme le montant format monértaire en nombre avec 2 décimales
 *
 * @param {string} monetaire Montant au format Monétaire
 * @return {number} Retourne le nombre correspondant au montant
 */
export function formatMonetaireEnNombre(monetaire) {
  if (!monetaire) return Number(0);
  if (typeof monetaire == 'string') {
    monetaire = monetaire
      .replace(/[^0-9.,-]/g, '')
      .replace(/,/g, '.')
      .replace(/(.*)\./, function (x) {
        return `${x.replace(/\./g, '')}.`;
      });
  }
  return Number(monetaire);
}

/**
 * Transforme le montant format monértaire en nombre avec 2 décimales
 *
 * @param {string} currency Montant au format Monétaire
 * @return {float} Retourne un float correspondant au montant
 */
export function parseFloatValueFromCurrency(currency) {
  let pattern = /`${String.fromCharCode(44)}`/;
  let value = currency.replace(new RegExp(pattern, 'g'), '.');
  // Retire le prefix et le suffix du montant ('$')
  pattern = /`${i18n.t('vMoney.prefix')}`/;
  value = value.replace(new RegExp(pattern, 'g'), '');
  pattern = /`${i18n.t('vMoney.suffix')}`/;
  value = value.replace(new RegExp(pattern, 'g'), '');
  value = value.replace(new RegExp(/ /, 'g'), '');
  return parseFloat(value);
}
// Supprimer tous les tags html et les espaces en trop
export function stripAllHtmlTag(htlmTxt) {
  const txt = htlmTxt.replace(/(<([^>]+)>)/gi, '');
  return removeExtraSpaces(txt);
}

/**
 * Formatte le numéro de téléphone
 *
 * @param {string} phoneNumber Numéro de téléphone
 * @return {string} Numéro de téléphone formatté
 */
export function formatteTelephone(phoneNumber) {
  return phoneNumber.replace(/[^\d]/g, '');
}

/**
 * Formattage de Date String au format Date Local
 *
 * @param {string} dateString Date au format string
 * @return {string} Date au format string local
 */
export function formatteDateStringToLocale(
  dateString,
  options = { year: 'numeric', month: 'long', day: 'numeric' }
) {
  // Conversion en Date en ajoutant le timezone local
  const currentOffset = createOffset(new Date());
  const d = new Date(`${dateString}T12:00:00${currentOffset}`);
  // Définition du format local
  const lang = getCurrentLanguage();
  const locale = `${lang}-CA`;
  // Conversion en String au format local 'en-CA' ou 'fr-CA' en fonction du format local
  return d.toLocaleDateString(locale, options);
}
/**
 * Ajoute zéro non significatif à gauche si < 10
 *
 * @param {string} value Valeur numérique (0..99)
 * @return {string} Valeur numérique avec 0 non significatif à gauche (00..99)
 */
function pad(value) {
  return value < 10 ? `0${value}` : value;
}
/**
 * Créer offset en fonction du TimeZone local de la date
 *
 * @param {Date} date Date au format string
 * @return {string} Offset local (GMT-0500 : heure normale de l’Est nord-américain)
 */
function createOffset(date) {
  const sign = date.getTimezoneOffset() > 0 ? '-' : '+';
  const offset = Math.abs(date.getTimezoneOffset());
  const hours = pad(Math.floor(offset / 60));
  const minutes = pad(offset % 60);
  return `${sign + hours}:${minutes}`;
}

// Décoder un jeton JWT et retourner le payload en json
export function parseJwt(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join('')
  );

  return JSON.parse(jsonPayload);
}

export function decodeHTMLText(html) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}

export function parseData(response) {
  try {
    return JSON.parse(response.data.MSGXX);
  } catch (error) {
    console.error('Error parsing MSGXX:', error);
    return null;
  }
}
